<template>
    <teleport to="body">
        <Modal
            :is-open="show"
            :title="`Transfer ${sourcePointsProgram?.programName} Points to ${destinationProgramNameComputed}`"
            @close="closeModal()"
        >
            <template #content>
                <div class="flex flex-col">
                    <div
                        class="justify-start items-center gap-2 inline-flex text-slate-500 -translate-y-6"
                    >
                        <Icon icon="stopwatch" />
                        <div class="text-sm font-normal leading-tight">
                            {{ transferTimeComputed }}
                        </div>
                    </div>
                    <div
                        class="self-stretch justify-center items-center md:items-start flex flex-col md:flex-row"
                    >
                        <div
                            class="grow shrink basis-0 md:p-8 flex-col justify-start items-center flex"
                        >
                            <div
                                class="flex-col justify-start items-start gap-2 flex"
                            >
                                <div
                                    class="text-slate-800 text-lg font-medium leading-7"
                                >
                                    {{
                                        $filters.formatThousand(
                                            totalUsersPoints
                                        )
                                    }}
                                </div>
                            </div>
                            <div
                                class="pt-4 w-20 md:w-24 flex-col justify-start items-start gap-2.5 flex"
                            >
                                <img :src="'/' + sourcePointsProgram.image" />
                            </div>
                        </div>
                        <!-- START ARROW 1 -->
                        <div
                            class="relative w-px h-[90px] md:h-[132px] md:pt-6 justify-center items-center md:items-start flex bg-gray-200 rotate-90 md:rotate-0"
                        >
                            <div
                                class="absolute w-8 h-8 md:w-11 md:h-11 bg-white dark:bg-slate-800 rounded-full shadow dark:shadow-slate-900/50 border border-slate-200 justify-center items-center gap-2.5 flex"
                            >
                                <i class="fa-light fa-arrow-right"></i>
                            </div>
                        </div>
                        <!-- END ARROW 1 -->
                        <!-- start rates -->
                        <div
                            class="grow shrink basis-0 md:p-8 flex-col justify-start items-center inline-flex"
                        >
                            <div
                                class="flex gap-1 items-baseline text-lg text-slate-500"
                            >
                                <span
                                    :class="[
                                        {
                                            'line-through': programBonus,
                                            'text-slate-800': !programBonus,
                                        },
                                    ]"
                                >
                                    {{ baseTransferRate }}
                                </span>

                                <span
                                    class="text-slate-800"
                                    v-if="programBonus"
                                >
                                    {{ programBonus.bonusRate }}
                                </span>
                                rate
                            </div>
                            <!-- end rates -->
                            <div
                                class="pt-2 flex-col justify-start items-center gap-2.5 flex"
                                v-if="programBonus"
                            >
                                <div
                                    class="px-3 py-0.5 bg-orange-50 rounded-full justify-center items-center gap-2.5 inline-flex"
                                >
                                    <div
                                        class="text-orange-600 text-xs font-medium leading-none"
                                    >
                                        {{ programBonus.bonusPercentage }}
                                        transfer bonus until
                                        {{
                                            $filters.formatShortDate(
                                                programBonus.bonusExpiration
                                            )
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- START ARROW 2 -->
                        <div
                            class="relative w-px h-[90px] md:h-[132px] md:pt-6 justify-center items-center md:items-start flex bg-gray-200 rotate-90 md:rotate-0"
                        >
                            <div
                                class="absolute w-8 h-8 md:w-11 md:h-11 bg-white dark:bg-slate-800 rounded-full shadow dark:shadow-slate-900/50 border border-slate-200 justify-center items-center gap-2.5 flex"
                            >
                                <i class="fa-light fa-arrow-right"></i>
                            </div>
                        </div>
                        <!-- END ARROW 2 -->
                        <div
                            class="grow shrink basis-0 md:p-8 flex-col justify-start items-center inline-flex"
                        >
                            <div
                                class="justify-start items-start gap-1 inline-flex"
                            >
                                <div
                                    class="text-slate-500 text-lg font-medium leading-7"
                                    :class="[
                                        {
                                            'line-through': programBonus,
                                            'text-slate-800': !programBonus,
                                        },
                                    ]"
                                >
                                    {{
                                        $filters.formatThousand(
                                            usersPointsTransfer
                                        )
                                    }}
                                </div>
                                <div
                                    v-if="programBonus"
                                    class="text-slate-800 text-lg font-medium leading-7"
                                >
                                    {{ $filters.formatThousand(sumPoints()) }}
                                </div>
                            </div>
                            <div
                                class="pt-2 flex-col justify-start items-start flex"
                                v-if="programBonus"
                            >
                                <div
                                    class="px-3 py-0.5 bg-orange-50 rounded-full justify-center items-center gap-2.5 inline-flex"
                                >
                                    <div
                                        class="text-orange-600 text-xs font-medium leading-none"
                                    >
                                        +{{
                                            $filters.formatThousand(
                                                programBonus.bonusPoints
                                            )
                                        }}
                                        extra points
                                    </div>
                                </div>
                            </div>
                            <div
                                class="pt-4 w-20 md:w-24 flex-col justify-start items-start gap-2.5 flex"
                            >
                                <img :src="'/' + destinationProgramImage" />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Modal>
    </teleport>
</template>

<script>
import { computed } from 'vue';
import dayjs from 'dayjs';
import { useHotelsStore } from '@/store/hotels';
import filters from '@/filters/index.js';
import { DialogTitle } from '@headlessui/vue';
import Modal from '@/components/ui/modal/ui-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    emits: ['close'],
    components: {
        DialogTitle,
        Icon,
        Modal,
    },
    props: {
        sourcePointsProgram: Object,
        totalUsersPoints: Number,
        destinationProgramName: String,
        destinationProgramImage: String,
        show: Boolean,
        transferTime: String,
    },
    emits: ['close'],

    setup(props, { emit }) {
        const hotelsStore = useHotelsStore();
        const destinationProgramNameComputed = computed(
            () => hotelsStore.pointsProgram.name || props.destinationProgramName
        );

        const transferTimeComputed = computed(() => {
            switch (props.transferTime) {
                case '0':
                    return `Transfers instantly`;
                case '1':
                    return 'Transfers in 1 day';
                default:
                    return `Transfers in ${props.transferTime} days`;
            }
        });

        const baseTransferRate = computed(() => {
            return `${props.sourcePointsProgram.transferRateFrom}:${props.sourcePointsProgram.transferRateTo}`;
        });

        const usersPointsTransfer = computed(() => {
            return filters.calculatePointsConversion(
                props.totalUsersPoints,
                props.sourcePointsProgram.transferRateTo /
                    props.sourcePointsProgram.transferRateFrom
            );
        });

        const bonusIsActive = (date) => {
            const today = dayjs();

            const expirationDate = dayjs(date).format('YYYY-MM-DD');
            const todayFormatted = today.format('YYYY-MM-DD');

            return expirationDate >= todayFormatted;
        };

        const programBonus = computed(() =>
            bonusIsActive(props.sourcePointsProgram.bonusExpiration)
                ? filters.getProgramBonusInfo(
                      props.sourcePointsProgram,
                      props.totalUsersPoints
                  )
                : null
        );

        const sumPoints = () => {
            if (programBonus.value) {
                return (
                    programBonus.value.bonusPoints + usersPointsTransfer.value
                );
            } else {
                return usersPointsTransfer.value.toFixed(0);
            }
        };

        const closeModal = () => {
            emit('close');
        };

        return {
            baseTransferRate,
            closeModal,
            destinationProgramNameComputed,
            programBonus,
            sumPoints,
            transferTimeComputed,
            usersPointsTransfer,
        };
    },
};
</script>
